import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MailCheck, Send } from "lucide-react";
import api from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function AdminForgotPassword() {
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const request = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post("/admin-recovery/forgot");
      setSent(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again in a minute.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-muted/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-xl" data-testid="admin-forgot-card">
        <BrandLogo />
        <h1 className="mt-6 font-serif-display text-3xl font-semibold text-secondary">Forgot password</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          We'll email a one-time reset link (valid 30 minutes) to the site owner's address.
        </p>

        {sent ? (
          <div className="mt-8 space-y-4" data-testid="admin-forgot-sent">
            <div className="flex items-start gap-2 rounded-xl border border-primary/40 bg-primary/5 p-4 text-sm text-secondary">
              <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <span>
                If recovery is available, a reset link has been emailed to the site owner. Check the inbox
                and follow the link within 30 minutes.
              </span>
            </div>
            <Link to="/admin/login" data-testid="admin-forgot-back-login" className="text-sm font-semibold text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="mt-8 space-y-4">
            <button
              data-testid="admin-forgot-submit"
              onClick={request}
              disabled={busy}
              className="btn-primary w-full justify-center"
            >
              <Send className="h-4 w-4" /> {busy ? "Sending…" : "Email me a reset link"}
            </button>
            {error && (
              <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive" data-testid="admin-forgot-error">
                {error}
              </div>
            )}
            <p className="text-xs text-muted-foreground" data-testid="admin-forgot-note">
              Note: this requires the email system to be enabled on the server. If no email arrives, use the{" "}
              <Link to="/admin/recovery" className="font-semibold text-primary hover:underline">
                emergency recovery page
              </Link>{" "}
              instead.
            </p>
            <Link to="/admin/login" data-testid="admin-forgot-back-login" className="inline-block text-sm font-semibold text-primary hover:underline">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
